<template>
  <div
    class="vs-input-number"
    :class="[
      `vs-input-number-size-${size}`,
      `vs-input-number-${color}`,
      $attrs.class,
      { isChangeValue },
    ]"
    :style="$attrs.style"
  >
    <button
      class="btn-less vs-input-number--button-less"
      :class="{ limit: Number(currentValue) <= Number(min) }"
      :style="{ background: resolvedColor }"
      :disabled="disabled"
      type="button"
      @mousedown="startRepeat(less, $event)"
      @mouseup="stopRepeat"
      @mouseleave="stopRepeat"
      @click="handleButtonClick(less)"
    >
      <vs-icon :icon-pack="iconPack" :icon="iconDec" />
    </button>
    <span v-if="label">{{ label }}</span>
    <input
      ref="input"
      class="vs-input-number--input"
      :style="styleInput"
      :disabled="isDisabled"
      :min="min"
      :max="max"
      type="number"
      :value="currentValue"
      v-bind="inputAttrs"
      @input="handleInput"
      @blur="handleBlur"
    />
    <button
      class="btn-plus vs-input-number--button-plus"
      :class="{
        limit: Number(currentValue) >= Number(max) && max !== null,
      }"
      :style="{ background: resolvedColor }"
      :disabled="disabled"
      type="button"
      @mousedown="startRepeat(plus, $event)"
      @mouseup="stopRepeat"
      @mouseleave="stopRepeat"
      @click="handleButtonClick(plus)"
    >
      <vs-icon :icon-pack="iconPack" :icon="iconInc" />
    </button>
  </div>
</template>

<script>
import colorUtils from "@/assets/utils/color";

export default {
  name: "VsInputNumber",
  inheritAttrs: false,
  props: {
    modelValue: {
      default: undefined,
    },
    value: {
      default: undefined,
    },
    color: {
      type: String,
      default: "primary",
    },
    label: {
      type: String,
      default: null,
    },
    max: {
      type: [Number, String],
      default: null,
    },
    min: {
      type: [Number, String],
      default: 0,
    },
    size: {
      type: String,
      default: null,
    },
    iconPack: {
      type: String,
      default: "material-icons",
    },
    iconDec: {
      type: String,
      default: "remove",
    },
    iconInc: {
      type: String,
      default: "add",
    },
    step: {
      type: [Number, String],
      default: 1,
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue", "input", "blur"],
  data() {
    return {
      isChangeValue: false,
      repeatInterval: null,
      repeated: false,
    };
  },
  computed: {
    currentValue() {
      return this.modelValue !== undefined ? this.modelValue : this.value;
    },
    disabled() {
      return this.isDisabled || this.$attrs.disabled !== undefined;
    },
    styleInput() {
      return {
        width: `${this.getLength}px`,
      };
    },
    getLength() {
      return this.currentValue ? String(this.currentValue).length * 9.1 : 0;
    },
    resolvedColor() {
      return colorUtils.isColor(this.color)
        ? `rgba(var(--vs-${this.color}), 1)`
        : colorUtils.getColor(this.color);
    },
    inputAttrs() {
      const attrs = { ...this.$attrs };
      [
        "class",
        "style",
        "disabled",
        "onInput",
        "onBlur",
      ].forEach((key) => delete attrs[key]);
      return attrs;
    },
  },
  watch: {
    currentValue() {
      this.isChangeValue = true;
      window.setTimeout(() => {
        this.isChangeValue = false;
      }, 200);
    },
  },
  beforeUnmount() {
    this.stopRepeat();
  },
  methods: {
    emitValue(value) {
      this.$emit("update:modelValue", value);
      this.$emit("input", value);
    },
    handleInput(event) {
      this.emitValue(event.target.value);
    },
    handleBlur(event) {
      if (this.max !== null && Number(this.currentValue) > Number(this.max)) {
        this.emitValue(this.max);
      } else if (Number(this.currentValue) < Number(this.min)) {
        this.emitValue(this.min);
      }
      this.$emit("blur", event);
    },
    handleButtonClick(action) {
      if (!this.repeated) action();
      this.repeated = false;
    },
    startRepeat(action, event) {
      if (event.button !== 0 || this.disabled) return;
      this.repeated = false;
      this.repeatInterval = window.setInterval(() => {
        this.repeated = true;
        action();
      }, 100);
    },
    stopRepeat() {
      if (this.repeatInterval !== null) {
        window.clearInterval(this.repeatInterval);
        this.repeatInterval = null;
      }
    },
    plus() {
      if (this.currentValue === "") {
        this.emitValue(this.fixPrecision(0));
      } else if (
        this.max === null ||
        Number(this.currentValue) < Number(this.max)
      ) {
        this.emitValue(
          this.fixPrecision(Number(this.currentValue) + Number(this.step))
        );
      }
    },
    less() {
      if (this.currentValue === "") {
        this.emitValue(this.fixPrecision(0));
      } else if (
        this.min === null ||
        Number(this.currentValue) > Number(this.min)
      ) {
        this.emitValue(
          this.fixPrecision(Number(this.currentValue) - Number(this.step))
        );
      }
    },
    fixPrecision(value) {
      const precision = String(this.step).split(".")[1];
      return value.toFixed(precision ? precision.length : 0);
    },
  },
};
</script>
